/**
 * Which rows of an incoming statement are already booked?
 *
 * No export carries a stable bank transaction id, and the same charge looks different in each
 * format: the card statement dates it by purchase (and card booking), George by the day it was
 * posted to the account - one business day after the card booking, three over a weekend - and the
 * text differs too. The EUR amount to the cent is the only thing every format agrees on.
 *
 * So every row goes through three questions, in this order:
 *
 *  1. Settlement? Paying off the card is a transfer, not an expense: excluded.
 *  2. Seen before by the same kind of source? Each row carries a fingerprint per source family
 *     (`export:…` / `statement:…`). Fingerprints are matched by count, not existence: two
 *     identical charges on one day are two charges, and a file with three of them next to two
 *     known ones creates exactly one.
 *  3. Seen before by the other kind of source? Same account, currency, sign and amount to the
 *     cent, and the export date within -2..+7 days of the statement's purchase date. Among those,
 *     merchant similarity, date closeness and matching FX fee/rate pick the twin, one to one.
 *
 * Whatever cannot be decided safely is created and flagged for review instead of being dropped:
 * an extra row is one click to merge, a lost charge is a missing receipt nobody looks for.
 */
import {
  isCardSettlement,
  merchantFromDescription,
  type SourceFamily,
  type StatementRow,
} from "./statement";

/** An already stored transaction, reduced to what matching needs. */
export type ExistingRow = {
  id: string;
  amountCents: number;
  currency: string;
  /** The date the app shows: the purchase date once a statement supplied it. */
  bookingDate: string;
  purchaseDate?: string;
  postingDate?: string;
  merchant: string;
  /** Fingerprints of every import that saw this row. Empty for rows from before reconciliation. */
  sourceKeys: string[];
  fee?: number;
  exchangeRate?: number;
};

export type ReviewReason = "ambiguous" | "weak_match" | "near_duplicate";

export type PlanItem =
  | { kind: "create"; row: StatementRow; key: string }
  | { kind: "known"; row: StatementRow; key: string; existingId: string }
  | { kind: "merge"; row: StatementRow; key: string; existingId: string; score: number }
  | { kind: "review"; row: StatementRow; key: string; duplicateOf: string; reason: ReviewReason }
  | { kind: "excluded"; row: StatementRow; reason: "settlement" };

export type PlanKind = PlanItem["kind"];

export type ImportPlan = {
  /** In the order of the incoming rows. */
  items: PlanItem[];
  counts: Record<PlanKind, number>;
};

/** How far the export may be dated from the statement's purchase date, in days. */
export const WINDOW = { before: 2, after: 7 } as const;
/** The usual gap between using the card and George posting it. */
const EXPECTED_LAG = 2;
/** Scores this close to the winner make the choice a coin toss. */
const TIE = 0.02;
/** Same-family rows this many days apart may be the same charge with edited text. */
const NEAR_DUPLICATE_DAYS = 3;

// --- text ----------------------------------------------------------------------------------

function normalize(text: string): string {
  return text.toUpperCase().replace(/\s+/g, " ").trim();
}

/** FNV-1a, enough to keep fingerprints short; collisions only matter within one account. */
function hash(text: string): string {
  let value = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    value ^= text.charCodeAt(index);
    value = Math.imul(value, 0x01000193) >>> 0;
  }
  return value.toString(16).padStart(8, "0");
}

/**
 * Identity of a row within its source family. Everything the source prints goes in, so the
 * George CSV, XLSX and JSON of one booking agree, while a different charge does not.
 */
export function fingerprint(row: StatementRow): string {
  const parts = [
    row.date,
    row.family === "statement" ? (row.cardBookingDate ?? "") : "",
    row.amountCents,
    row.currency.toUpperCase(),
    normalize(row.counterparty),
    normalize(row.description),
    normalize(row.reference),
  ];
  return `${row.family}:${hash(parts.join("|"))}`;
}

export function familyOfKey(key: string): SourceFamily | null {
  if (key.startsWith("export:")) return "export";
  if (key.startsWith("statement:")) return "statement";
  return null;
}

/** Tokens that say nothing about which merchant it is. */
const NOISE = new Set([
  "WWW",
  "COM",
  "NET",
  "ORG",
  "AT",
  "DE",
  "IO",
  "GMBH",
  "AG",
  "LTD",
  "INC",
  "SL",
  "SA",
  "CO",
]);

function tokens(text: string): Set<string> {
  return new Set(
    text
      .toUpperCase()
      .replace(/Ä/g, "AE")
      .replace(/Ö/g, "OE")
      .replace(/Ü/g, "UE")
      .replace(/ß/g, "SS")
      .split(/[^A-Z0-9]+/)
      .filter((token) => token.length >= 2 && !NOISE.has(token)),
  );
}

/**
 * Share of the shorter name found in the longer one. A statement prints "NAME-CHEAP.COM* MRME0L
 * WWW.NAMECHEAP" where George prints "NAME-CHEAP.COM* MRME0L": that is a full match, while the
 * order code after the asterisk still tells two Namecheap charges apart.
 */
export function merchantSimilarity(left: string, right: string): number {
  const a = tokens(left);
  const b = tokens(right);
  if (a.size === 0 || b.size === 0) return 0;
  let shared = 0;
  for (const token of a) if (b.has(token)) shared += 1;
  return shared / Math.min(a.size, b.size);
}

/**
 * Order numbers and other references in a merchant string ("MRME0L", "G184882152"): tokens of
 * four or more characters that contain a digit.
 */
function references(text: string): Set<string> {
  return new Set([...tokens(text)].filter((token) => token.length >= 4 && /\d/.test(token)));
}

/**
 * Both names carry a reference and none of them match: two different orders from the same
 * merchant, however alike the rest of the name reads. Such a pair is never merged on its own.
 */
export function referencesConflict(left: string, right: string): boolean {
  const a = references(left);
  const b = references(right);
  if (a.size === 0 || b.size === 0) return false;
  for (const reference of a) if (b.has(reference)) return false;
  return true;
}

// --- dates ---------------------------------------------------------------------------------

function days(from: string, to: string): number {
  return Math.round(
    (Date.parse(`${to.slice(0, 10)}T00:00:00Z`) - Date.parse(`${from.slice(0, 10)}T00:00:00Z`)) /
      86_400_000,
  );
}

function familiesOf(existing: ExistingRow): Set<SourceFamily> {
  const families = new Set<SourceFamily>();
  for (const key of existing.sourceKeys) {
    const family = familyOfKey(key);
    if (family) families.add(family);
  }
  return families;
}

type Fit = { delta: number; dateScore: number } | null;

/** How well the dates of an incoming row and an existing row of the other family fit together. */
function dateFit(row: StatementRow, existing: ExistingRow): Fit {
  const families = familiesOf(existing);
  if (families.size === 0) {
    // A row from before reconciliation: we do not know which date it carries, so be symmetric.
    const delta = days(existing.bookingDate, row.date);
    if (Math.abs(delta) > WINDOW.after) return null;
    return { delta, dateScore: 1 - Math.abs(delta) / WINDOW.after };
  }
  const statementDate =
    row.family === "statement" ? row.date : (existing.purchaseDate ?? existing.bookingDate);
  const exportDate =
    row.family === "export" ? row.date : (existing.postingDate ?? existing.bookingDate);
  const delta = days(statementDate, exportDate);
  if (delta < -WINDOW.before || delta > WINDOW.after) return null;
  return {
    delta,
    dateScore: 1 - Math.min(Math.abs(delta - EXPECTED_LAG), WINDOW.after) / WINDOW.after,
  };
}

/** Both sides know the FX details: they must agree, or it is a different charge. */
function fxAgreement(row: StatementRow, existing: ExistingRow): "agree" | "conflict" | "unknown" {
  let compared = false;
  if (row.fee !== undefined && existing.fee !== undefined) {
    if (Math.abs(row.fee - existing.fee) > 0.005) return "conflict";
    compared = true;
  }
  if (row.exchangeRate !== undefined && existing.exchangeRate !== undefined) {
    // The statement prints four decimals, George seven: compare at the coarser one.
    if (Math.abs(row.exchangeRate - existing.exchangeRate) > 0.00051) return "conflict";
    compared = true;
  }
  return compared ? "agree" : "unknown";
}

/** Two stored rows that agree on everything matching looks at: merging into either is the same. */
function interchangeable(a: ExistingRow, b: ExistingRow): boolean {
  return (
    a.amountCents === b.amountCents &&
    a.bookingDate === b.bookingDate &&
    a.purchaseDate === b.purchaseDate &&
    a.postingDate === b.postingDate &&
    normalize(a.merchant) === normalize(b.merchant) &&
    [...a.sourceKeys].sort().join() === [...b.sourceKeys].sort().join()
  );
}

// --- planning ------------------------------------------------------------------------------

type Pair = {
  index: number;
  existing: ExistingRow;
  score: number;
  delta: number;
  strong: boolean;
};

export function planImport(
  rows: StatementRow[],
  existingRows: ExistingRow[],
  options: { excludeSettlements?: boolean } = {},
): ImportPlan {
  const items: Array<PlanItem | null> = rows.map(() => null);
  const claimed = new Set<string>();

  // 1 + 2: settlements, and rows the same kind of source has delivered before.
  const keys = rows.map(fingerprint);
  const seen = new Map<string, number>();
  const pending: number[] = [];
  rows.forEach((row, index) => {
    const key = keys[index]!;
    if (options.excludeSettlements && isCardSettlement(row)) {
      items[index] = { kind: "excluded", row, reason: "settlement" };
      return;
    }
    const occurrence = (seen.get(key) ?? 0) + 1;
    seen.set(key, occurrence);
    const holders = existingRows
      .filter((existing) => existing.sourceKeys.includes(key))
      .sort((a, b) => a.id.localeCompare(b.id));
    const holder = holders[occurrence - 1];
    if (holder) {
      items[index] = { kind: "known", row, key, existingId: holder.id };
      claimed.add(holder.id);
    } else {
      pending.push(index);
    }
  });

  // 3: the same charge from the other kind of source.
  const pairs: Pair[] = [];
  for (const index of pending) {
    const row = rows[index]!;
    for (const existing of existingRows) {
      if (claimed.has(existing.id)) continue;
      if (familiesOf(existing).has(row.family)) continue;
      if (existing.amountCents !== row.amountCents) continue;
      if (existing.currency.toUpperCase() !== row.currency.toUpperCase()) continue;
      const fit = dateFit(row, existing);
      if (!fit) continue;
      const fx = fxAgreement(row, existing);
      if (fx === "conflict") continue;
      const name = `${row.merchant} ${row.location}`;
      const similarity = merchantSimilarity(name, existing.merchant);
      const conflict = referencesConflict(name, existing.merchant);
      pairs.push({
        index,
        existing,
        delta: fit.delta,
        score: 0.6 * similarity + 0.3 * fit.dateScore + 0.1 * (fx === "agree" ? 1 : 0),
        // Matching FX details cannot overrule different order numbers: two orders of the same
        // amount on the same day share the day's rate and fee.
        strong: !conflict && (similarity >= 0.5 || fx === "agree"),
      });
    }
  }

  // One to one, most convincing pairs first.
  const byConfidence = (a: Pair, b: Pair) =>
    b.score - a.score ||
    Math.abs(a.delta) - Math.abs(b.delta) ||
    a.existing.id.localeCompare(b.existing.id);
  const assigned = new Map<number, Pair>();
  for (const pair of pairs.filter((candidate) => candidate.strong).sort(byConfidence)) {
    if (assigned.has(pair.index) || claimed.has(pair.existing.id)) continue;
    assigned.set(pair.index, pair);
    claimed.add(pair.existing.id);
  }

  for (const [index, pair] of assigned) {
    const row = rows[index]!;
    const key = keys[index]!;
    // A twin nobody else wanted, scoring as well: no way to know which of the two this is -
    // unless the two stored rows are indistinguishable, in which case either one is right.
    const rival = pairs.find(
      (other) =>
        other.index === index &&
        other.strong &&
        other.existing.id !== pair.existing.id &&
        !claimed.has(other.existing.id) &&
        other.score >= pair.score - TIE &&
        !interchangeable(other.existing, pair.existing),
    );
    items[index] = rival
      ? { kind: "review", row, key, duplicateOf: pair.existing.id, reason: "ambiguous" }
      : {
          kind: "merge",
          row,
          key,
          existingId: pair.existing.id,
          score: Math.round(pair.score * 100) / 100,
        };
  }

  for (const index of pending) {
    if (items[index]) continue;
    const row = rows[index]!;
    const key = keys[index]!;

    // Same amount in the window, but the names do not say it is the same merchant.
    const weak = pairs
      .filter((pair) => pair.index === index && !claimed.has(pair.existing.id))
      .sort(byConfidence)[0];
    if (weak) {
      items[index] = {
        kind: "review",
        row,
        key,
        duplicateOf: weak.existing.id,
        reason: "weak_match",
      };
      continue;
    }

    // The same kind of source delivered something very close: perhaps the bank edited the text.
    const near = existingRows.find(
      (existing) =>
        !claimed.has(existing.id) &&
        familiesOf(existing).has(row.family) &&
        existing.amountCents === row.amountCents &&
        Math.abs(
          days(
            row.family === "export"
              ? (existing.postingDate ?? existing.bookingDate)
              : (existing.purchaseDate ?? existing.bookingDate),
            row.date,
          ),
        ) <= NEAR_DUPLICATE_DAYS &&
        merchantSimilarity(row.merchant, existing.merchant) >= 0.5 &&
        !referencesConflict(row.merchant, existing.merchant),
    );
    items[index] = near
      ? { kind: "review", row, key, duplicateOf: near.id, reason: "near_duplicate" }
      : { kind: "create", row, key };
  }

  const final = items as PlanItem[];
  const counts: Record<PlanKind, number> = {
    create: 0,
    known: 0,
    merge: 0,
    review: 0,
    excluded: 0,
  };
  for (const item of final) counts[item.kind] += 1;
  return { items: final, counts };
}

// --- writing -------------------------------------------------------------------------------

/** The stored fields of a transaction that an import may fill in. */
export type TransactionFields = {
  bookingDate: string;
  purchaseDate: string;
  cardBookingDate: string;
  postingDate: string;
  valueDate: string;
  amount: number;
  currency: string;
  counterparty: string;
  description: string;
  reference: string;
  location: string;
  foreignAmount: number;
  foreignCurrency: string;
  exchangeRate: number;
  fee: number;
  externalId: string;
  sourceKeys: string[];
  confirmedBy: string[];
};

/** A new transaction from an incoming row. The app shows the purchase date when there is one. */
export function newTransaction(
  row: StatementRow,
  key: string,
  importId: string,
): TransactionFields {
  return {
    bookingDate: row.purchaseDate ?? row.date,
    purchaseDate: row.purchaseDate ?? "",
    cardBookingDate: row.cardBookingDate ?? "",
    postingDate: row.postingDate ?? "",
    valueDate: row.valueDate ?? "",
    amount: row.amountCents / 100,
    currency: row.currency,
    counterparty: row.counterparty || row.merchant,
    description: row.description,
    reference: row.reference,
    location: row.location,
    foreignAmount: row.foreignAmount ?? 0,
    foreignCurrency: row.foreignCurrency ?? "",
    exchangeRate: row.exchangeRate ?? 0,
    fee: row.fee ?? 0,
    externalId: key,
    sourceKeys: [key],
    confirmedBy: [importId],
  };
}

const blank = (value: unknown) =>
  value === undefined ||
  value === null ||
  value === "" ||
  value === 0 ||
  (typeof value === "string" && value.trim() === "");

/**
 * What a matching row adds to a stored one. Only empty fields are filled - a stored value, the
 * attached receipt, the status and the notes are never touched - plus the new key and import.
 * A card statement moves the shown date to the purchase date, which is what receipts carry.
 */
export function mergedTransaction(
  existing: Partial<TransactionFields>,
  row: StatementRow,
  key: string,
  importId: string,
): Partial<TransactionFields> {
  const incoming = newTransaction(row, key, importId);
  const patch: Partial<TransactionFields> = {};
  const fill = <K extends keyof TransactionFields>(field: K) => {
    if (blank(existing[field]) && !blank(incoming[field])) patch[field] = incoming[field];
  };
  for (const field of [
    "purchaseDate",
    "cardBookingDate",
    "postingDate",
    "valueDate",
    "counterparty",
    "description",
    "reference",
    "location",
    "foreignAmount",
    "foreignCurrency",
    "exchangeRate",
    "fee",
  ] as const) {
    fill(field);
  }
  if (row.family === "statement" && row.purchaseDate) patch.bookingDate = row.purchaseDate;
  patch.sourceKeys = [...new Set([...(existing.sourceKeys ?? []), key])];
  patch.confirmedBy = [...new Set([...(existing.confirmedBy ?? []), importId])];
  return patch;
}

/** A stored transaction as matching sees it. */
export function toExistingRow(record: {
  id: string;
  amount: number;
  currency?: string;
  bookingDate: string;
  purchaseDate?: string;
  postingDate?: string;
  counterparty?: string;
  description?: string;
  sourceKeys?: unknown;
  fee?: number;
  exchangeRate?: number;
}): ExistingRow {
  return {
    id: record.id,
    amountCents: Math.round(record.amount * 100),
    currency: record.currency || "EUR",
    bookingDate: record.bookingDate.slice(0, 10),
    purchaseDate: record.purchaseDate ? record.purchaseDate.slice(0, 10) : undefined,
    postingDate: record.postingDate ? record.postingDate.slice(0, 10) : undefined,
    merchant: [record.counterparty, merchantFromDescription(record.description ?? "")]
      .filter(Boolean)
      .join(" "),
    sourceKeys: Array.isArray(record.sourceKeys) ? (record.sourceKeys as string[]) : [],
    fee: record.fee || undefined,
    exchangeRate: record.exchangeRate || undefined,
  };
}

/** The parts of a stored transaction a person may have worked on. */
export type WorkedFields = { status: string; document: string; notes?: string };

/**
 * Folds a row flagged as a possible duplicate into its twin when the user confirms it: the twin
 * gains whatever it was missing, the keys and imports of both, and - if only the duplicate had
 * been matched to a receipt - that match. The duplicate is then deleted by the caller.
 */
export function combineDuplicate(
  target: Partial<TransactionFields> & WorkedFields,
  duplicate: Partial<TransactionFields> & WorkedFields,
): Partial<TransactionFields> & Partial<WorkedFields> {
  const patch: Partial<TransactionFields> & Partial<WorkedFields> = {};
  for (const field of [
    "purchaseDate",
    "cardBookingDate",
    "postingDate",
    "valueDate",
    "counterparty",
    "description",
    "reference",
    "location",
    "foreignAmount",
    "foreignCurrency",
    "exchangeRate",
    "fee",
  ] as const) {
    if (blank(target[field]) && !blank(duplicate[field])) {
      Object.assign(patch, { [field]: duplicate[field] });
    }
  }
  if (duplicate.purchaseDate && !target.purchaseDate) patch.bookingDate = duplicate.purchaseDate;
  patch.sourceKeys = [...new Set([...(target.sourceKeys ?? []), ...(duplicate.sourceKeys ?? [])])];
  patch.confirmedBy = [
    ...new Set([...(target.confirmedBy ?? []), ...(duplicate.confirmedBy ?? [])]),
  ];
  if (target.status === "unmatched" && duplicate.status !== "unmatched") {
    patch.status = duplicate.status;
    patch.document = duplicate.document;
  }
  return patch;
}
